// src/hooks/useLocalizedNavigate.js
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isSupportedLang } from '../i18n';

const DEFAULT_LANG = 'en';

/**
 * Returns a navigate function that prefixes paths with the current :lang.
 * Use inside routes under /:lang. If :lang is missing, uses DEFAULT_LANG.
 */
export function useLocalizedNavigate() {
  const navigate = useNavigate();
  const { lang } = useParams();
  const currentLang = isSupportedLang(lang) ? lang : DEFAULT_LANG;

  return useCallback((to, options) => {
    if (typeof to === 'number') {
      navigate(to, options);
      return;
    }
    if (typeof to === 'string') {
      const path = to.startsWith('/') ? to : `/${to}`;
      const localized = path === '/' ? `/${currentLang}` : `/${currentLang}${path}`;
      navigate(localized, options);
    } else {
      const { pathname, search, hash } = to;
      const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
      const localized = path === '/' ? `/${currentLang}` : `/${currentLang}${path}`;
      navigate({ ...to, pathname: localized }, options);
    }
  }, [navigate, currentLang]);
}

/** Get current :lang from route params; use inside routes under /:lang. */
export function useLangParam() {
  const { lang } = useParams();
  return isSupportedLang(lang) ? lang : DEFAULT_LANG;
}
